import * as PIXI from 'pixi.js';
import { dialogConfig } from '@/config/DialogConfig';
import { SoundManager } from '@/systems/SoundManager';
import { StoryManager } from '@/systems/StoryManager';
import { InputManager } from '@/systems/InputManager';
import type { DialogChoice, DialogNode, DialogTree } from '@/types';

// Interface for queued actions that should be executed after dialog closes
interface QueuedAction {
  type:
    | 'addPoints'
    | 'deductPoints'
    | 'addAffection'
    | 'addHostility'
    | 'deductAffection'
    | 'deductHostility'
    | 'nextDay'
    | 'previousDay'
    | 'addState'
    | 'removeState'
    | 'addCustomValue'
    | 'custom';
  value?: any;
  customFunction?: () => void;
}

let previousDialogTimeoutId:number|undefined

export class DialogSystem {
  private container: PIXI.Container;
  private cutsceneContainer: PIXI.Container;
  private backgroundSprite: PIXI.Sprite | null = null;
  private characterSprite: PIXI.Sprite | null = null;
  private background!: PIXI.Graphics;
  private dialogBoxSprite: PIXI.Sprite | null = null;
  private dialogBoxTexture: PIXI.Texture | null = null;
  private interactionArea!: PIXI.Graphics; // Invisible overlay for interactions
  private nameText!: PIXI.Text;
  private dialogText!: PIXI.Text;
  private choicesContainer!: PIXI.Container;
  private continueText!: PIXI.Text;
  private isVisible: boolean = false;
  private screenWidth: number;
  private screenHeight: number;
  private currentDialogTree: DialogTree | null = null;
  private currentNodeId: string | null = null;
  private choiceTexts: PIXI.Text[] = [];
  private choiceButtons: PIXI.Container[] = [];
  private choiceBackgrounds: PIXI.Graphics[] = [];

  // Track current NPC for interaction state management
  private currentNPCName: string | null = null;

  // Action queue system
  private actionQueue: QueuedAction[] = [];

  // Performance optimizations
  private lastDialogSize: { width: number; height: number } = { width: 0, height: 0 };
  private choiceTextPool: PIXI.Text[] = [];
  private choiceButtonPool: PIXI.Container[] = [];
  private choiceBackgroundPool: PIXI.Graphics[] = [];
  private readonly maxChoicePoolSize = 10;
  private animationId: number | null = null;

  private isTyping:boolean|undefined;

  // Image loading cache
  private imageCache: Map<string, PIXI.Texture> = new Map();

  // Dialog padding configuration
  private readonly topPadding = 60; // Extra padding at top to position text lower

  // Hidden timeout choice management
  private choiceTimeoutId: number | null = null;

  constructor(screenWidth: number, screenHeight: number) {
    this.screenWidth = screenWidth;
    this.screenHeight = screenHeight;

    this.container = new PIXI.Container();
    this.cutsceneContainer = new PIXI.Container();
    this.createDialogBox();
    this.setupInteractivity();
    this.hide();
    // Load the dialog box texture after initialization
    this.initializeDialogBoxTexture();
  }

  private async initializeDialogBoxTexture(): Promise<void> {
    try {
      this.dialogBoxTexture = await this.loadImage('assets/misc/dialog-box.png');
      // Create the sprite now that texture is loaded
      if (this.dialogBoxTexture) {
        this.dialogBoxSprite = new PIXI.Sprite(this.dialogBoxTexture);
        // Insert the sprite at the correct position in the container
        const backgroundIndex = this.container.getChildIndex(this.background);
        this.container.addChildAt(this.dialogBoxSprite, backgroundIndex);
        
        // Force an update to use the sprite now that it's loaded
        this.lastDialogSize = { width: 0, height: 0 }; // Reset to force update
        this.updateDialogSize();
        
        console.log('Dialog box texture loaded successfully');
      }
    } catch (error) {
      console.warn('Failed to load dialog box texture, using fallback:', error);
    }
  }

  private createDialogBox(): void {
    // Dialog box background (fallback graphics for when image isn't loaded)
    this.background = new PIXI.Graphics();

    // Create invisible interaction area that will be positioned on top
    this.interactionArea = new PIXI.Graphics();
    this.interactionArea.interactive = true;
    this.interactionArea.cursor = 'pointer';
    this.interactionArea.alpha = 0; // Completely invisible

    // Now call updateDialogSize after both graphics objects are created
    this.updateDialogSize();

    // Define consistent padding values
    const verticalSpacing = 16;

    // NPC name text
    const buttonWidth = Math.min(this.screenWidth * 0.6, 600); // Use same width as buttons
    this.nameText = new PIXI.Text('NPC Name', {
      // Use placeholder text for proper height calculation
      fontFamily: dialogConfig.font.family,
      fontSize: dialogConfig.font.nameSize,
      fill: dialogConfig.colors.name,
      fontWeight: 'bold',
      wordWrap: true,
      wordWrapWidth: buttonWidth,
      dropShadow: {
        color: dialogConfig.colors.shadow,
        blur: 2,
        distance: 1,
        alpha: 1,
        angle: Math.PI / 4,
      },
      align: 'left', // Left align text
    });
    this.nameText.anchor.set(0, 0); // Left aligned, top aligned
    this.nameText.x = this.screenWidth / 2; // Center on screen
    this.nameText.y = this.topPadding;
    this.nameText.resolution = dialogConfig.font.resolution;
    this.nameText.interactive = false;

    // Dialog text
    this.dialogText = new PIXI.Text('Dialog text will appear here.', {
      // Use placeholder text
      fontFamily: dialogConfig.font.family,
      fontSize: dialogConfig.font.dialogSize,
      fill: dialogConfig.colors.dialog,
      wordWrap: true,
      wordWrapWidth: buttonWidth, // Use same width as buttons
      leading: 4,
      dropShadow: {
        color: dialogConfig.colors.shadow,
        blur: 1,
        distance: 1,
        alpha: 1,
        angle: Math.PI / 4,
      },
      align: 'left', // Left align text
    });
    this.dialogText.anchor.set(0, 0); // Left aligned, top aligned
    this.dialogText.x = this.screenWidth / 2; // Center on screen
    this.dialogText.y = this.topPadding + this.nameText.height + verticalSpacing;
    this.dialogText.resolution = dialogConfig.font.resolution;
    this.dialogText.interactive = false;

    // Choices container
    this.choicesContainer = new PIXI.Container();
    this.choicesContainer.x = 0; // Will be positioned dynamically
    this.choicesContainer.y = this.topPadding + this.nameText.height + verticalSpacing + 60; // Space for dialog text
    this.choicesContainer.interactive = false;

    // Continue instruction
    this.continueText = new PIXI.Text(dialogConfig.text.continue, {
      fontFamily: dialogConfig.font.family,
      fontSize: dialogConfig.font.continueSize,
      fill: dialogConfig.colors.continue,
      dropShadow: {
        color: dialogConfig.colors.shadow,
        blur: 1,
        distance: 1,
        alpha: 1,
        angle: Math.PI / 4,
      },
      align: 'right', // Right align text
    });
    this.continueText.anchor.set(1, 0); // Right aligned, top aligned
    this.continueText.resolution = dialogConfig.font.resolution;
    this.continueText.interactive = false;

    // Add all elements to container (order matters for layering)
    this.container.addChild(this.cutsceneContainer); // Cutscene elements first (background)
    this.container.addChild(this.background); // Always add graphics background initially
    this.container.addChild(this.nameText);
    this.container.addChild(this.dialogText);
    this.container.addChild(this.choicesContainer);
    this.container.addChild(this.continueText);
    this.container.addChild(this.interactionArea); // Interaction area on top
  }

  private setupInteractivity(): void {
    // Make the main container interactive for general event handling
    this.container.interactive = true;
  }

  private updateDialogSize(hideBox?: boolean, overrideText?: string): void {
    const padding = 20;
    const verticalSpacing = 16;

    // Background spans full screen width
    const boxWidth = this.screenWidth;

    const hasChoices = this.choicesContainer && this.choicesContainer.children.length > 0;

    // Calculate dynamic height based on content
    let contentHeight = this.topPadding; // Top padding

    // Add name text height (use fallback if not available)
    const nameTextHeight = this.nameText?.height || dialogConfig.font.nameSize + 4;
    contentHeight += nameTextHeight + verticalSpacing;

    // Add dialog text height (estimate based on wrapped text)
    const buttonWidth = Math.min(this.screenWidth * 0.6, 600);
    const textToMeasure = overrideText !== undefined ? overrideText : (this.dialogText?.text || '');
    const dialogTextHeight = textToMeasure
      ? this.estimateTextHeight(textToMeasure, buttonWidth)
      : dialogConfig.font.dialogSize + 4;
    contentHeight += dialogTextHeight + verticalSpacing;

    // Add choices height if present
    if (hasChoices) {
      const numChoices = this.choicesContainer.children.length;
      const buttonHeight = 32;
      const buttonSpacing = 8;
      const choicesHeight = numChoices * (buttonHeight + buttonSpacing) - buttonSpacing;
      contentHeight += choicesHeight + verticalSpacing;
    }

    // Add space for continue text
    const continueTextHeight = this.continueText?.height || dialogConfig.font.continueSize + 4;
    contentHeight += continueTextHeight + padding; // Continue text height + bottom padding

    // Use calculated height or minimum height
    const boxHeight = Math.max(contentHeight, dialogConfig.box.heightDefault);

    // Position at the bottom of the screen, spanning full width
    const x = 0;
    const y = this.screenHeight - boxHeight;

    // Optimize: Only redraw if size actually changed
    if (this.lastDialogSize.width === boxWidth && this.lastDialogSize.height === boxHeight) {
      return;
    }

    this.lastDialogSize.width = boxWidth;
    this.lastDialogSize.height = boxHeight;

    // Update dialog box sprite or fallback graphics
    const canUseSprite = this.dialogBoxSprite && this.dialogBoxTexture && this.dialogBoxSprite.parent && !hideBox;
    
    if (canUseSprite && this.dialogBoxSprite) {
      // Scale and xposition the dialog box sprite to fit the dialog area
      this.dialogBoxSprite.width = boxWidth;
      this.dialogBoxSprite.height = boxHeight;
      this.dialogBoxSprite.x = 0;
      this.dialogBoxSprite.y = 0;
      this.dialogBoxSprite.visible = true;
      
      // Hide the graphics background when using sprite
      this.background.clear();
      this.background.visible = false;
      
      console.log('Using dialog box sprite:', boxWidth, 'x', boxHeight);
    } else {
      // Use graphics background as fallback or when hiding
      this.background.clear();
      this.background.visible = true;
      if (this.dialogBoxSprite) {
        this.dialogBoxSprite.visible = false;
      }
      
      // Draw translucent black background without borders
      if (hideBox) {
        this.background.rect(0, 0, boxWidth, boxHeight).fill({ color: 0x000000, alpha: 0 }); // Black with 0% opacity
      } else {
        this.background.rect(0, 0, boxWidth, boxHeight).fill({ color: 0x000000, alpha: 0.7 }); // Black with 70% opacity
      }
      
      console.log('Using fallback graphics background. Sprite available:', !!this.dialogBoxSprite, 'Texture available:', !!this.dialogBoxTexture, 'In parent:', !!this.dialogBoxSprite?.parent);
    }

    // Position container
    console.log(`Dialog Positioning: Screen H=${this.screenHeight}, Box H=${boxHeight}, Y=${y}`);
    
    this.container.x = x;
    this.container.y = y;

    // Update interaction area to cover the entire dialog
    this.interactionArea.clear();

    // Check if there are choices to determine interaction area coverage
    const hasChoicesForInteraction =
      this.choicesContainer && this.choicesContainer.children.length > 0;

    if (!hasChoicesForInteraction) {
      // Cover the entire dialog when there are no choices
      this.interactionArea.beginFill(dialogConfig.colors.shadow, 0);
      this.interactionArea.drawRect(0, 0, boxWidth, boxHeight);
      this.interactionArea.endFill();
    }

    // Explicitly set hit area to ensure proper mouse detection
    if (!hasChoicesForInteraction) {
      this.interactionArea.hitArea = new PIXI.Rectangle(0, 0, boxWidth, boxHeight);
    } else {
      this.interactionArea.hitArea = null;
    }

    // Update continue text position with bounds checking
    if (this.continueText) {
      const buttonWidth = Math.min(this.screenWidth * 0.6, 600);
      const buttonAreaEndX = (this.screenWidth + buttonWidth) / 2;

      this.continueText.x = buttonAreaEndX; // Right align with buttons
      this.continueText.y = boxHeight - this.continueText.height - padding; // Position within dialog margin
    }

    // Update dialog text wrap width
    if (this.dialogText) {
      const buttonWidth = Math.min(this.screenWidth * 0.6, 600);
      this.dialogText.style.wordWrapWidth = buttonWidth;
    }
  }

  public show(npcName: string, dialog: string): void {
    this.nameText.text = npcName;
    this.dialogText.text = dialog;
    this.continueText.text = dialogConfig.text.continue;
    this.isVisible = true;
    this.container.visible = true;

    this.container.visible = true;

    // Hide interaction button when dialog opens
    InputManager.getInstance().setInteractButtonVisible(false);
    InputManager.getInstance().setMobileControlsVisible(false);

    // Update positioning with consistent spacing and left alignment with buttons
    const verticalSpacing = 16;

    // Align name and dialog text with button area (left-aligned)
    // Align name and dialog text with button area (left-aligned)
    const BUTTON_MAX_WIDTH = 600;
    // On mobile landscape, use more width (90%) to save vertical space
    const widthRatio = (this.screenWidth < 768 || InputManager.getInstance().isMobile()) ? 0.9 : 0.6;
    
    const buttonWidth = Math.min(this.screenWidth * widthRatio, BUTTON_MAX_WIDTH);
    const buttonAreaStartX = (this.screenWidth - buttonWidth) / 2;

    this.nameText.x = buttonAreaStartX;
    this.nameText.y = this.topPadding;

    // Center dialog text
    this.dialogText.x = buttonAreaStartX;
    const nameTextHeight = this.nameText?.height || dialogConfig.font.nameSize + 4;
    this.dialogText.y = this.topPadding + nameTextHeight + verticalSpacing;

    // Update positions after text change
    this.updateDialogSize(npcName.length > 0 || dialog.length > 0);
    this.updateContinueTextPosition();
  }

  public async showDialogTree(dialogTree: DialogTree, npcName?: string): Promise<void> {
    this.currentDialogTree = dialogTree;
    this.currentNodeId = dialogTree.startDialogId;
    this.currentNPCName = npcName || null;
    await this.displayCurrentNode();
    this.isVisible = true;
    this.container.visible = true;

    // Hide interaction button
    InputManager.getInstance().setInteractButtonVisible(false);
    InputManager.getInstance().setMobileControlsVisible(false);
  }

  public async showInitialDayDialog(dialogTree: DialogTree): Promise<void> {
    // Show initial day dialog without an NPC context
    this.currentDialogTree = dialogTree;
    this.currentNodeId = dialogTree.startDialogId;
    this.currentNPCName = null; // No NPC for initial day dialogs
    await this.displayCurrentNode();
    this.isVisible = true;
    this.container.visible = true;

    // Hide interaction button
    InputManager.getInstance().setInteractButtonVisible(false);
    InputManager.getInstance().setMobileControlsVisible(false);
  }

  public async showSimpleDialog(
    speaker: string,
    text: string,
    npcName?: string,
    characterImage?: string,
    backgroundImage?: string,
    isEnd?: boolean,
  ): Promise<void> {
    this.currentNPCName = npcName || null;
    this.currentDialogTree = null;
    this.currentNodeId = null;

    // Reset containers
    this.cutsceneContainer.removeChildren();
    this.choicesContainer.removeChildren();

    this.nameText.text = speaker;
    this.dialogText.text = text;
    // Set appropriate continue text based on whether this was an ending dialog
    this.continueText.text = isEnd ? dialogConfig.text.continueEnd : dialogConfig.text.continue;
    this.isVisible = true;
    this.container.visible = true;

    // Hide interaction button
    // Hide interaction button
    InputManager.getInstance().setInteractButtonVisible(false);
    InputManager.getInstance().setMobileControlsVisible(false);

    // Update positioning
    const verticalSpacing = 16;
    const BUTTON_MAX_WIDTH = 600;
    // On mobile landscape, use more width (90%) to save vertical space
    const widthRatio = (this.screenWidth < 768 || InputManager.getInstance().isMobile()) ? 0.9 : 0.6;
    
    const buttonWidth = Math.min(this.screenWidth * widthRatio, BUTTON_MAX_WIDTH);
    const buttonAreaStartX = (this.screenWidth - buttonWidth) / 2;

    this.nameText.x = buttonAreaStartX;
    this.nameText.y = this.topPadding;

    this.dialogText.x = buttonAreaStartX;
    const nameTextHeight = this.nameText?.height || dialogConfig.font.nameSize + 4;
    this.dialogText.y = this.topPadding + nameTextHeight + verticalSpacing;

    this.setupSimpleDialogClick();
    this.updateDialogSize();
    this.updateContinueTextPosition();

    // Setup backdrop images if provided
    await this.setupSimpleDialogImages(characterImage, backgroundImage);
  }

  public async showItemPickupDialog(itemId: string, quantity: number): Promise<void> {
    const storyManager = StoryManager.getInstance();
    const itemConfig = await import('@/config/ItemsConfig').then(m => m.getItemConfig(itemId));

    if (!itemConfig) {
      console.warn(`Item config not found for itemId: ${itemId}`);
      return;
    }

    // Add item to inventory
    storyManager.addItem(itemId, quantity);

    // Create pickup message
    const message = itemConfig.dialog || `${quantity}x ${itemConfig.name} acquired!`;

    // Reset containers
    this.cutsceneContainer.removeChildren();
    this.choicesContainer.removeChildren();

    this.nameText.text = ''; // No speaker for item pickup
    this.dialogText.text = message;
    this.continueText.text = 'Click to continue';
    this.isVisible = true;
    this.container.visible = true;

    // Hide interaction button
    InputManager.getInstance().setInteractButtonVisible(false);
    InputManager.getInstance().setMobileControlsVisible(false);

    // Update positioning
    const verticalSpacing = 16;
    const BUTTON_MAX_WIDTH = 600;
    // On mobile landscape, use more width (90%) to save vertical space
    const widthRatio = (this.screenWidth < 768 || InputManager.getInstance().isMobile()) ? 0.9 : 0.6;
    
    const buttonWidth = Math.min(this.screenWidth * widthRatio, BUTTON_MAX_WIDTH);
    const buttonAreaStartX = (this.screenWidth - buttonWidth) / 2;

    this.nameText.x = buttonAreaStartX;
    this.nameText.y = this.topPadding;

    this.dialogText.x = buttonAreaStartX;
    const nameTextHeight = this.nameText?.height || dialogConfig.font.nameSize + 4;
    this.dialogText.y = this.topPadding + nameTextHeight + verticalSpacing;

    // Make dialog box transparent/hidden for item pickup
    this.updateDialogSize(true); // true = hide box
    this.updateContinueTextPosition();

    // Setup click to close (non-skippable means they must click, but it still closes on click)
    this.setupSimpleDialogClick();
  }

  private async setupSimpleDialogImages(
    characterImage?: string,
    backgroundImage?: string,
  ): Promise<void> {
    // Clear existing backdrop content
    this.cutsceneContainer.removeChildren();
    this.backgroundSprite = null;
    this.characterSprite = null;

    // Load and display background image
    if (backgroundImage) {
      try {
        const backgroundTexture = await this.loadImage(backgroundImage);
        this.backgroundSprite = new PIXI.Sprite(backgroundTexture);

        // Scale background to cover entire viewport (like CSS background-size: cover)
        const bgScaleX = this.screenWidth / backgroundTexture.width;
        const bgScaleY = this.screenHeight / backgroundTexture.height;
        const bgScale = Math.max(bgScaleX, bgScaleY);
        this.backgroundSprite.scale.set(bgScale);

        // Position background to start from absolute screen coordinates (0,0)
        // Since cutsceneContainer is child of container which is positioned, we need to offset
        this.backgroundSprite.x =
          -this.container.x + (this.screenWidth - this.backgroundSprite.width) / 2;
        this.backgroundSprite.y =
          -this.container.y + (this.screenHeight - this.backgroundSprite.height) / 2;

        // Add a stronger overlay for more atmospheric, spooky effect
        this.backgroundSprite.alpha = 1.0;

        this.cutsceneContainer.addChild(this.backgroundSprite);
      } catch (error) {
        console.warn('Failed to setup background image:', error);
      }
    }

    // Load and display character image
    if (characterImage) {
      try {
        const characterTexture = await this.loadImage(characterImage);
        this.characterSprite = new PIXI.Sprite(characterTexture);

        // Scale character to fit nicely on the right side
        const isMobile = InputManager.getInstance().isMobile();
        const maxCharacterHeight = this.screenHeight * (isMobile ? 1 : 0.8); // Larger on mobile
        const maxCharacterWidth = this.screenWidth * (isMobile ? 0.6 : 0.4); // Allow more width on mobile
        const charScale = Math.min(
          maxCharacterHeight / characterTexture.height,
          maxCharacterWidth / characterTexture.width,
        );
        this.characterSprite.scale.set(charScale);

        // Position character on right side, accounting for container position
        const rightSideX = this.screenWidth - this.characterSprite.width - 50; // 50px from right edge
        this.characterSprite.x = -this.container.x + rightSideX;
        // Position character so its bottom is at the bottom of the screen (y=0 relative to screen)
        this.characterSprite.y =
          -this.container.y + (this.screenHeight - this.characterSprite.height);

        this.cutsceneContainer.addChild(this.characterSprite);
      } catch (error) {
        console.warn('Failed to setup character image:', error);
      }
    }
  }

  private setupSimpleDialogClick(): void {
    // Remove any existing event handlers
    this.interactionArea.off('pointerup');
    this.interactionArea.off('pointerover');
    this.interactionArea.off('pointerout');

    // Make the interaction area active
    this.interactionArea.interactive = true;
    this.interactionArea.cursor = 'pointer';
    this.interactionArea.alpha = 0;

    // Add click handler to close the dialog
    this.interactionArea.on('pointerup', () => {
      this.hide(); // Use hide() instead of hideWithoutRecording() to process any queued actions
    });

    // Add visual feedback
    this.interactionArea.on('pointerover', () => {
      this.continueText.tint = dialogConfig.colors.hover;
      if (this.dialogBoxSprite?.visible) {
        this.dialogBoxSprite.alpha = 0.95;
      } else {
        this.background.alpha = 0.98;
      }
    });

    this.interactionArea.on('pointerout', () => {
      this.continueText.tint = dialogConfig.colors.white;
      if (this.dialogBoxSprite?.visible) {
        this.dialogBoxSprite.alpha = 1.0;
      } else {
        this.background.alpha = 1.0;
      }
    });
  }

  private async initiateDialogAnimation(completeDialog:string){
    if(previousDialogTimeoutId){
      clearTimeout(previousDialogTimeoutId)
    }
    this.dialogText.text = ""
    this.isTyping = true
    this.playDialogAnimation(completeDialog)
  }

  private async playDialogAnimation(completeDialog:string){

    const textToType = completeDialog
    const typingSpeed = 5; // Speed in milliseconds (lower is faster)

    if (this.dialogText.text.length < textToType.length) {
      this.dialogText.text += textToType.charAt(this.dialogText.text.length);
      previousDialogTimeoutId = window.setTimeout(()=>this.playDialogAnimation(completeDialog), typingSpeed);
    }else{
      this.isTyping = false
      // Show choices after typing is complete if there are any
      if (this.choicesContainer && this.choicesContainer.children.length > 0) {
        this.choicesContainer.visible = true;
      }
    }

  }

  private async displayCurrentNode(): Promise<void> {
    if (!this.currentDialogTree || !this.currentNodeId) return;

    const node = this.currentDialogTree.nodes[this.currentNodeId];
    if (!node) return;

    // Clear any existing choice timeout
    this.clearChoiceTimeout();

    // Play the sound effect if specified in the dialog node
    if (node.sfx) {
      const soundManager = SoundManager.getInstance();
      // Add 'sfx_' prefix to soundEffect ID when playing
      soundManager.play(`sfx_${node.sfx}`);
      console.log(`Playing dialog sound effect: ${node.sfx}`);
    }else{
      const soundManager = SoundManager.getInstance();
      soundManager.play(`sfx_click`);
    }

    // Check for victory/game over triggers on node display
    if (node.isVictory) {
      // Show the dialog first, then trigger victory when it ends
      // We'll handle this in the continue/close logic
    }

    if (node.isGameOver) {
      // Show the dialog first, then trigger game over when it ends
      // We'll handle this in the continue/close logic
    }

    // Define consistent dimensions
    // Define consistent dimensions
    const BUTTON_MAX_WIDTH = 600;
    // On mobile landscape, use more width (90%) to save vertical space
    const widthRatio = (this.screenWidth < 768 || InputManager.getInstance().isMobile()) ? 0.9 : 0.6;
    
    const buttonWidth = Math.min(this.screenWidth * widthRatio, BUTTON_MAX_WIDTH);
    const verticalSpacing = 16;
    const buttonAreaStartX = (this.screenWidth - buttonWidth) / 2;

    this.nameText.text = node.speaker;
    
    // Update text wrap width immediately after setting text
    this.nameText.style.wordWrapWidth = buttonWidth;
    this.dialogText.style.wordWrapWidth = buttonWidth;

    // PIXI v8 automatically handles text updates when text or style changes

    // Clear previous choices
    this.choicesContainer.removeChildren();

    if (node.choices && node.choices.length > 0) {
      this.createChoices(node.choices);
      // Hide choices initially, show them after typing finishes
      this.choicesContainer.visible = false;
      this.continueText.text = dialogConfig.text.continueChoice;
      
      // Setup timeout for hidden timeout choice if present
      this.setupChoiceTimeout(node.choices);
    } else {
      this.continueText.text = node.isEnd
        ? dialogConfig.text.continueEnd
        : dialogConfig.text.continue;
    }

    // Start dialog animation after choices are setup (so if it finishes instantly, choices are ready)
    console.log("TRYING TO INPUT", node.text)
    this.initiateDialogAnimation(node.text)

    // Update positioning using the already defined variables
    this.nameText.x = buttonAreaStartX;
    this.nameText.y = this.topPadding;

    this.dialogText.x = buttonAreaStartX;
    const nameTextHeight = this.nameText?.height || dialogConfig.font.nameSize + 4;

    // Use full node text for height calculation to ensure consistent box size
    const dialogTextHeight = node.text
      ? this.estimateTextHeight(node.text, buttonWidth)
      : dialogConfig.font.dialogSize + 4;

    this.dialogText.y = this.topPadding + nameTextHeight + verticalSpacing;
    
    this.choicesContainer.x = this.screenWidth / 2;
    this.choicesContainer.y =
      this.topPadding + nameTextHeight + verticalSpacing + dialogTextHeight + verticalSpacing;

    this.setupContinueClick();
    this.updateDialogSize(node.speaker.length === 0 && node.text.length === 0 && !node.choices, node.text);
    this.updateContinueTextPosition(); // Update position after text changes

    // Setup backdrop images after dialog positioning is calculated
    await this.setupCutsceneImages(node);

    if (node.storyAction) {
      this.queueAction({
        type: node.storyAction.type as QueuedAction['type'],
        value: node.storyAction.value,
        customFunction: node.storyAction.customFunction,
      });
    }

    if(node.storyActions){
      for(let action of node.storyActions){
        this.queueAction({
          type: action.type as QueuedAction['type'],
          value: action.value,
          customFunction: action.customFunction,
        });
      }
    }

    if (node.itemReward){
      console.log("GAMESTATE CLICKING")

      if (node.itemReward) {
        console.log("GAMESTATE getting item", node.itemReward)
        const storyManager = StoryManager.getInstance();
        storyManager.addItem(node.itemReward.itemId, node.itemReward.quantity);
        console.log(
          `Item reward: ${node.itemReward.quantity}x ${node.itemReward.itemId}`,
        );
      }

    }

  }

  private updateContinueTextPosition(): void {
    if (!this.continueText) return;

    const padding = 20;
    const BUTTON_MAX_WIDTH = 600;
    // On mobile landscape, use more width (90%) to save vertical space
    const widthRatio = this.screenWidth < 768 ? 0.9 : 0.6;
    const buttonWidth = Math.min(this.screenWidth * widthRatio, BUTTON_MAX_WIDTH);

    // Calculate dynamic height based on content
    let contentHeight = this.topPadding; // Top padding
    const nameTextHeight = this.nameText?.height || dialogConfig.font.nameSize + 4;
    contentHeight += nameTextHeight + 16; // Name + spacing

    const dialogTextHeight = this.dialogText?.text
      ? this.estimateTextHeight(this.dialogText.text, buttonWidth)
      : dialogConfig.font.dialogSize + 4;
    contentHeight += dialogTextHeight + 16; // Dialog text + spacing

    if (this.choicesContainer && this.choicesContainer.children.length > 0) {
      const numChoices = this.choicesContainer.children.length;
      const buttonHeight = 32;
      const buttonSpacing = 8;
      const choicesHeight = numChoices * (buttonHeight + buttonSpacing) - buttonSpacing;
      contentHeight += choicesHeight + 16; // Choices + spacing
    }

    const continueTextHeight = this.continueText?.height || dialogConfig.font.continueSize + 4;
    const boxHeight = Math.max(
      contentHeight + continueTextHeight + padding,
      dialogConfig.box.heightDefault,
    );

    // Align continue text to the right edge of button area
    const buttonAreaEndX = (this.screenWidth + buttonWidth) / 2;

    this.continueText.x = buttonAreaEndX;
    this.continueText.y = boxHeight - this.continueText.height - padding;
  }

  private createChoices(choices: DialogChoice[]): void {
    this.choiceTexts = []; // Clear previous choice texts
    this.choiceButtons = []; // Clear previous choice buttons
    this.choiceBackgrounds = []; // Clear previous choice backgrounds

    // Use reasonable width for buttons but center them on screen
    const BUTTON_MAX_WIDTH = 600;
    // On mobile landscape, use more width (90%) to save vertical space
    const widthRatio = (this.screenWidth < 768 || InputManager.getInstance().isMobile()) ? 0.9 : 0.6;
    const buttonWidth = Math.min(this.screenWidth * widthRatio, BUTTON_MAX_WIDTH);
    
    const isMobile = (this.screenWidth < 768 || InputManager.getInstance().isMobile());
    const buttonHeight = isMobile ? 40 : 32; // Slightly larger for touch
    const buttonSpacing = 8;
    const buttonPadding = 10; // Internal button padding

    const storyManager = StoryManager.getInstance();
    let visibleChoiceIndex = 0;

    choices.forEach((choice, originalIndex) => {
      // Skip hidden timeout choices - they shouldn't be rendered
      if (choice.isHiddenTimeout) {
        return;
      }

      // Check if choice meets requirements
      if (!storyManager.checkChoiceRequirements(choice)) {
        return; // Skip this choice if requirements not met
      }

      // Create button container
      const buttonContainer = this.getPooledChoiceButton() || new PIXI.Container();

      // Create or reuse button background
      let buttonBackground = this.getPooledChoiceBackground();
      if (!buttonBackground) {
        buttonBackground = new PIXI.Graphics();
      }

      // Determine if this is a special choice
      const isSpecial = choice.isSpecial || false;

      // Draw button background with special styling for special choices
      this.drawButtonBackground(buttonBackground, buttonWidth, buttonHeight, false, isSpecial);

      // Use pooled text object if available, otherwise create new one
      let choiceText = this.getPooledChoiceText();
      if (!choiceText) {
        choiceText = new PIXI.Text('', {
          fontFamily: dialogConfig.font.family,
          fontSize: dialogConfig.font.choiceSize,
          fill: dialogConfig.colors.choice,
          wordWrap: true,
          wordWrapWidth: buttonWidth - buttonPadding * 2, // Leave padding inside button
          dropShadow: {
            color: dialogConfig.colors.shadow,
            blur: 1,
            distance: 1,
            alpha: 1,
            angle: Math.PI / 4,
          },
          align: 'center', // Keep choices centered within their buttons
        });
        choiceText.resolution = dialogConfig.font.resolution;
      }

      // Update text content and style, add special indicator for special choices
      const displayText = isSpecial ? `✨ ${choice.text}` : choice.text;
      choiceText.text = displayText;
      choiceText.style.wordWrapWidth = buttonWidth - buttonPadding * 2;
      choiceText.tint = isSpecial ? 0xffd700 : dialogConfig.colors.white; // Gold for special choices

      // Center text in button
      choiceText.anchor.set(0.5, 0.5); // Center anchor
      choiceText.x = buttonWidth / 2; // Center horizontally
      choiceText.y = buttonHeight / 2; // Center vertically

      // Clear container and add components
      buttonContainer.removeChildren();
      buttonContainer.addChild(buttonBackground);
      buttonContainer.addChild(choiceText);

      // Position button - center each button horizontally relative to the choices container
      buttonContainer.x = -buttonWidth / 2; // Since choicesContainer is centered, offset by half button width
      buttonContainer.y = visibleChoiceIndex * (buttonHeight + buttonSpacing);
      buttonContainer.interactive = true;
      buttonContainer.cursor = 'pointer';

      // Clear any existing event listeners
      buttonContainer.removeAllListeners();

      // Add hover effects
      buttonContainer.on('pointerover', () => {
        this.drawButtonBackground(buttonBackground, buttonWidth, buttonHeight, true, isSpecial);
        choiceText.tint = isSpecial ? 0xffffff : dialogConfig.colors.hover; // White for special on hover
      });

      buttonContainer.on('pointerout', () => {
        this.drawButtonBackground(buttonBackground, buttonWidth, buttonHeight, false, isSpecial);
        choiceText.tint = isSpecial ? 0xffd700 : dialogConfig.colors.white; // Restore original colors
      });

      // Handle click/tap - use original index for selection
      buttonContainer.on('pointerup', async () => {
        await this.selectChoice(originalIndex);
      });

      // Add to choices container
      this.choicesContainer.addChild(buttonContainer);

      // Store references
      this.choiceTexts.push(choiceText);
      this.choiceButtons.push(buttonContainer);
      this.choiceBackgrounds.push(buttonBackground);

      visibleChoiceIndex++;
    });
  }

  private getPooledChoiceText(): PIXI.Text | null {
    return this.choiceTextPool.pop() || null;
  }

  private getPooledChoiceButton(): PIXI.Container | null {
    return this.choiceButtonPool.pop() || null;
  }

  private getPooledChoiceBackground(): PIXI.Graphics | null {
    return this.choiceBackgroundPool.pop() || null;
  }

  private drawButtonBackground(
    graphics: PIXI.Graphics,
    width: number,
    height: number,
    isHovered: boolean,
    isSpecial: boolean = false,
  ): void {
    graphics.clear();

    let backgroundColor: number;
    let borderColor: number;

    if (isSpecial) {
      // Special gold/magical theme for special choices
      backgroundColor = isHovered ? 0x8b6914 : 0x6d4c0c; // Dark gold backgrounds
      borderColor = isHovered ? 0xffd700 : 0xb8860b; // Gold borders
    } else {
      // Normal dark brown theme
      backgroundColor = isHovered ? 0x3e2723 : 0x2c1810;
      borderColor = isHovered ? dialogConfig.colors.hover : dialogConfig.colors.border;
    }

    const borderWidth = isHovered ? 2 : 1;

    // Draw button shadow with multiple layers for depth
    graphics.beginFill(dialogConfig.colors.shadow, 0.5);
    graphics.drawRoundedRect(3, 3, width, height, 6);
    graphics.endFill();

    graphics.beginFill(dialogConfig.colors.shadow, 0.3);
    graphics.drawRoundedRect(2, 2, width, height, 6);
    graphics.endFill();

    // Draw button background
    graphics.beginFill(backgroundColor, 0.9);
    graphics.drawRoundedRect(0, 0, width, height, 6);
    graphics.endFill();

    // Add subtle gradient overlay
    if (isHovered) {
      const overlayColor = isSpecial ? 0xffd700 : dialogConfig.colors.hover;
      graphics.beginFill(overlayColor, 0.1);
      graphics.drawRoundedRect(0, 0, width, height / 2, 6);
      graphics.endFill();
    }

    // Draw button border
    graphics.lineStyle(borderWidth, borderColor, 0.8);
    graphics.drawRoundedRect(0, 0, width, height, 6);

    // Add inner highlight for more depth
    const innerHighlightColor = isSpecial ? 0xffd700 : dialogConfig.colors.innerBorder;
    graphics.lineStyle(1, innerHighlightColor, 0.4);
    graphics.drawRoundedRect(1, 1, width - 2, height - 2, 5);

    // Add sparkle effect for special choices
    if (isSpecial && isHovered) {
      graphics.lineStyle(1, 0xffffff, 0.6);
      graphics.drawRoundedRect(0.5, 0.5, width - 1, height - 1, 5.5);
    }
  }

  private estimateTextHeight(text: string, wrapWidth: number): number {
    if (!text || text.trim() === '') return dialogConfig.font.dialogSize + 4; // Return minimum height for empty text

    // Create a temporary text object to measure height
    const tempText = new PIXI.Text(text, {
      fontFamily: dialogConfig.font.family,
      fontSize: dialogConfig.font.dialogSize,
      wordWrap: true,
      wordWrapWidth: wrapWidth,
      leading: 4,
    });

    const height = tempText.height || dialogConfig.font.dialogSize + 4; // Fallback height
    tempText.destroy();
    return height;
  }

  private async loadImage(imagePath: string): Promise<PIXI.Texture> {
    // Check cache first
    if (this.imageCache.has(imagePath)) {
      const cachedTexture = this.imageCache.get(imagePath);
      if (cachedTexture) {
        return cachedTexture;
      }
    }

    try {
      // Load texture from path using PIXI.Assets.load (PIXI v8)
      const texture = await PIXI.Assets.load(imagePath);
      this.imageCache.set(imagePath, texture);
      return texture;
    } catch (error) {
      console.warn(`Failed to load image: ${imagePath}`, error);
      // Return a placeholder or empty texture
      return PIXI.Texture.EMPTY;
    }
  }

  private async setupCutsceneImages(node: DialogNode): Promise<void> {
    // Clear existing backdrop content
    this.cutsceneContainer.removeChildren();
    this.backgroundSprite = null;
    this.characterSprite = null;

    // Load and display background image
    if (node.backgroundImage) {
      try {
        const backgroundTexture = await this.loadImage(node.backgroundImage);
        this.backgroundSprite = new PIXI.Sprite(backgroundTexture);

        // Scale background to cover entire viewport (like CSS background-size: cover)
        const bgScaleX = this.screenWidth / backgroundTexture.width;
        const bgScaleY = this.screenHeight / backgroundTexture.height;
        const bgScale = Math.max(bgScaleX, bgScaleY);
        this.backgroundSprite.scale.set(bgScale);

        // Position background to start from absolute screen coordinates (0,0)
        // Since cutsceneContainer is child of container which is positioned, we need to offset
        this.backgroundSprite.x =
          -this.container.x + (this.screenWidth - this.backgroundSprite.width) / 2;
        this.backgroundSprite.y =
          -this.container.y + (this.screenHeight - this.backgroundSprite.height) / 2;

        // Add a stronger overlay for more atmospheric, spooky effect
        this.backgroundSprite.alpha = 1.0;

        this.cutsceneContainer.addChild(this.backgroundSprite);
      } catch (error) {
        console.warn('Failed to setup background image:', error);
      }
    }

    // Load and display character image
    if (node.characterImage) {
      try {
        const characterTexture = await this.loadImage(node.characterImage);
        this.characterSprite = new PIXI.Sprite(characterTexture);

        // Scale character to fit nicely on the right side
        const isMobile = InputManager.getInstance().isMobile();
        const maxCharacterHeight = this.screenHeight * (isMobile ? 1 : 0.9); // Larger on mobile
        const maxCharacterWidth = this.screenWidth * (isMobile ? 0.6 : 1.0); // Allow more width on mobile
        const charScale = Math.min(
          maxCharacterHeight / characterTexture.height,
          maxCharacterWidth / characterTexture.width,
        );
        this.characterSprite.scale.set(charScale);

        // Position character on right side, accounting for container position
        const rightSideX = this.screenWidth - this.characterSprite.width - 50; // 50px from right edge
        this.characterSprite.x = -this.container.x + rightSideX;
        // Position character so its bottom is at the bottom of the screen (y=0 relative to screen)
        this.characterSprite.y =
          -this.container.y + (this.screenHeight - this.characterSprite.height);

        this.cutsceneContainer.addChild(this.characterSprite);
      } catch (error) {
        console.warn('Failed to setup character image:', error);
      }
    }
  }

  private returnChoiceTextToPool(textObject: PIXI.Text): void {
    if (this.choiceTextPool.length < this.maxChoicePoolSize) {
      textObject.removeAllListeners();
      textObject.visible = true;
      textObject.parent?.removeChild(textObject);
      this.choiceTextPool.push(textObject);
    } else {
      textObject.destroy();
    }
  }

  private returnChoiceButtonToPool(buttonContainer: PIXI.Container): void {
    if (this.choiceButtonPool.length < this.maxChoicePoolSize) {
      buttonContainer.removeAllListeners();
      buttonContainer.visible = true;
      buttonContainer.parent?.removeChild(buttonContainer);
      this.choiceButtonPool.push(buttonContainer);
    } else {
      buttonContainer.destroy();
    }
  }

  private returnChoiceBackgroundToPool(backgroundGraphics: PIXI.Graphics): void {
    if (this.choiceBackgroundPool.length < this.maxChoicePoolSize) {
      backgroundGraphics.clear();
      backgroundGraphics.parent?.removeChild(backgroundGraphics);
      this.choiceBackgroundPool.push(backgroundGraphics);
    } else {
      backgroundGraphics.destroy();
    }
  }

  /**
   * Setup timeout for hidden timeout choice if one exists
   */
  private setupChoiceTimeout(choices: DialogChoice[]): void {
    // Find the first hidden timeout choice
    const hiddenTimeoutChoice = choices.find(choice => choice.isHiddenTimeout);
    if (!hiddenTimeoutChoice) return;

    const originalIndex = choices.indexOf(hiddenTimeoutChoice);
    const timeoutSeconds = hiddenTimeoutChoice.timeoutSeconds || 5; // Default to 5 seconds

    console.log(`DialogSystem: Setting up hidden timeout choice (index ${originalIndex}) with ${timeoutSeconds}s delay`);

    this.choiceTimeoutId = window.setTimeout(() => {
      console.log(`DialogSystem: Timeout expired, auto-selecting hidden choice (index ${originalIndex})`);
      this.selectChoice(originalIndex);
    }, timeoutSeconds * 1000);
  }

  /**
   * Clear any active choice timeout
   */
  private clearChoiceTimeout(): void {
    if (this.choiceTimeoutId !== null) {
      clearTimeout(this.choiceTimeoutId);
      this.choiceTimeoutId = null;
      console.log('DialogSystem: Cleared choice timeout');
    }
  }

  private async selectChoice(choiceIndex: number): Promise<void> {
    // Clear timeout when any choice is selected
    this.clearChoiceTimeout();

    if (this.isTyping) {
      console.log('DialogSystem: Ignoring choice selection while typing');
      return;
    }

    if (!this.currentDialogTree || !this.currentNodeId) {
      console.warn('DialogSystem: No active dialog tree or current node');
      return;
    }

    const node = this.currentDialogTree.nodes[this.currentNodeId];
    if (!node) {
      console.error(`DialogSystem: Node '${this.currentNodeId}' not found in dialog tree`);
      return;
    }

    if (!node.choices || choiceIndex >= node.choices.length || choiceIndex < 0) {
      console.error(
        `DialogSystem: Invalid choice index ${choiceIndex} for node '${this.currentNodeId}'`,
      );
      return;
    }

    const selectedChoice = node.choices[choiceIndex];

    // Check if this is a special choice that should be unlocked
    if (selectedChoice.isSpecial && this.currentNPCName) {
      const storyManager = StoryManager.getInstance();
      const dialogId = selectedChoice.nextDialogId || this.currentNodeId || 'unknown';
      storyManager.unlockSpecialDialog(dialogId, this.currentNPCName, selectedChoice.specialType);
    }

    // Store victory/game over triggers for after dialog completion
    const pendingVictory = selectedChoice.isVictory;
    const pendingGameOver = selectedChoice.isGameOver;
    const victoryDetail = {
      reason: 'Victory dialog reached!',
      npc: this.currentNPCName,
      dialog: selectedChoice.nextDialogId || this.currentNodeId,
    };
    const gameOverDetail = {
      reason: 'Game over dialog reached!',
      npc: this.currentNPCName,
      dialog: selectedChoice.nextDialogId || this.currentNodeId,
    };

    // Queue story action if present (instead of executing immediately)
    if (selectedChoice.storyAction) {
      this.queueAction({
        type: selectedChoice.storyAction.type as QueuedAction['type'],
        value: selectedChoice.storyAction.value,
        customFunction: selectedChoice.storyAction.customFunction,
      });
    }

    // Queue story action if present (instead of executing immediately)
    if (selectedChoice.storyActions) {
      for(let action of selectedChoice.storyActions){
        this.queueAction({
          type: action.type as QueuedAction['type'],
          value: action.value,
          customFunction: action.customFunction,
        });
      }

    }

    // Queue legacy action if present
    if (selectedChoice.action) {
      this.queueAction({
        type: 'custom',
        customFunction: selectedChoice.action,
      });
    }

    // Handle item reward if present
    if (selectedChoice.itemReward) {
      const storyManager = StoryManager.getInstance();
      storyManager.addItem(selectedChoice.itemReward.itemId, selectedChoice.itemReward.quantity);
      console.log(
        `Item reward: ${selectedChoice.itemReward.quantity}x ${selectedChoice.itemReward.itemId}`,
      );
    }

    // Handle item consumption if present
    if (selectedChoice.consumeItems) {
      const storyManager = StoryManager.getInstance();
      for (const consumeItem of selectedChoice.consumeItems) {
        const success = storyManager.removeItem(consumeItem.itemId, consumeItem.quantity);
        if (success) {
          console.log(
            `Consumed: ${consumeItem.quantity}x ${consumeItem.itemId}`,
          );
        } else {
          console.warn(
            `Failed to consume: ${consumeItem.quantity}x ${consumeItem.itemId} (insufficient quantity)`,
          );
        }
      }
    }

    // Navigate to next dialog or close
    if (selectedChoice.isEnd) {
      // Check for victory/game over triggers before closing
      if (pendingVictory) {
        window.dispatchEvent(new CustomEvent('gameVictory', { detail: victoryDetail }));
      } else if (pendingGameOver) {
        window.dispatchEvent(new CustomEvent('gameOver', { detail: gameOverDetail }));
      }
      this.hide();
    } else if (selectedChoice.nextDialogId) {
      if (this.currentDialogTree.nodes[selectedChoice.nextDialogId]) {
        this.currentNodeId = selectedChoice.nextDialogId;
        await this.displayCurrentNode();
      } else {
        console.error(`DialogSystem: Next dialog node '${selectedChoice.nextDialogId}' not found`);
        this.hide();
      }
    } else {
      // Check for victory/game over triggers before closing
      if (pendingVictory) {
        window.dispatchEvent(new CustomEvent('gameVictory', { detail: victoryDetail }));
      } else if (pendingGameOver) {
        window.dispatchEvent(new CustomEvent('gameOver', { detail: gameOverDetail }));
      }
      this.hide();
    }
  }

  /**
   * Add an action to the queue to be executed after dialog closes
   */
  private queueAction(action: QueuedAction): void {
    this.actionQueue.push(action);
    this.processActionQueue()
    console.log('DialogSystem: Queued action:', action.type);
  }

  /**
   * Process all queued actions in order
   */
  private processActionQueue(): void {
    if (this.actionQueue.length === 0) return;

    console.log(`DialogSystem: Processing ${this.actionQueue.length} queued actions`);
    const storyManager = StoryManager.getInstance();

    for (const action of this.actionQueue) {
      try {
        switch (action.type) {
          case 'addCustomValue':
            console.log("CUSTOMVALUES ADDING", action.value)
            if (typeof action.value === 'object' ) {
              storyManager.addCustomValue(action.value.key, action.value.value);
            }
            break;
          case 'addState':
            console.log("CUSTOMSTATE ADDING", action.value)
            if (action.value !== undefined) {
              storyManager.addGameState(action.value+"");
            }
            break;
          case 'removeState':
            if (action.value !== undefined) {
              storyManager.removeGameState(action.value+"");
            }
            break;
          case 'addPoints':
            if (action.value !== undefined) {
              storyManager.addPoints(action.value);
            } else {
              console.warn('DialogSystem: addPoints action missing value');
            }
            break;
          case 'deductPoints':
            if (action.value !== undefined) {
              storyManager.deductPoints(action.value);
            } else {
              console.warn('DialogSystem: deductPoints action missing value');
            }
            break;
          case 'addAffection':
            if (action.value !== undefined) {
              storyManager.addPoints(action.value); // Positive values go to affection
            } else {
              console.warn('DialogSystem: addAffection action missing value');
            }
            break;
          case 'addHostility':
            if (action.value !== undefined) {
              storyManager.deductPoints(action.value); // Positive deduction goes to hostility
            } else {
              console.warn('DialogSystem: addHostility action missing value');
            }
            break;
          case 'deductAffection':
            if (action.value !== undefined) {
              storyManager.addPoints(-action.value); // Negative values reduce affection
            } else {
              console.warn('DialogSystem: deductAffection action missing value');
            }
            break;
          case 'deductHostility':
            if (action.value !== undefined) {
              storyManager.deductPoints(-action.value); // Negative deduction reduces hostility
            } else {
              console.warn('DialogSystem: deductHostility action missing value');
            }
            break;
          case 'custom':
            if (action.customFunction) {
              action.customFunction();
            } else {
              console.warn('DialogSystem: custom action missing customFunction');
            }
            break;
          case 'nextDay':
            console.log('DialogSystem: Advancing to next day (from queue)');
            storyManager.nextDay(action.value);
            break;
          case 'previousDay':
            console.log('DialogSystem: Going to previous day (from queue)');
            storyManager.previousDay();
            break;
          default:
            console.warn(`DialogSystem: Unknown queued action type: ${action.type}`);
        }
      } catch (error) {
        console.error(`DialogSystem: Error executing queued action ${action.type}:`, error);
      }
    }

    // Clear the queue after processing
    this.actionQueue = [];
  }

  /**
   * Clear the action queue without processing (used for cleanup)
   */
  private clearActionQueue(): void {
    this.actionQueue = [];
  }

  private setupContinueClick(): void {
    // Remove any existing continue click handlers
    this.interactionArea.off('pointerup');
    this.interactionArea.off('pointerover');
    this.interactionArea.off('pointerout');
    this.background.off('pointerup');
    this.background.off('pointerover');
    this.background.off('pointerout');

    if (!this.currentDialogTree || !this.currentNodeId) return;

    const node = this.currentDialogTree.nodes[this.currentNodeId];
    if (!node) return;

    // Only set up click handler for non-choice dialogs
    if (!node.choices || node.choices.length === 0) {
      // Make the interaction area active
      this.interactionArea.interactive = true;
      this.interactionArea.cursor = 'pointer';
      this.interactionArea.alpha = 0; // Completely invisible

      // Define the continue action
      const continueAction = async () => {
        if (this.isTyping) {
          console.log('DialogSystem: Ignoring continue click while typing');
          return;
        }

        // Record interaction before any navigation that might trigger day changes
        this.recordCurrentInteraction();

        if (node.isEnd) {
          // Check for victory/game over triggers before closing
          if (node.isVictory) {
            window.dispatchEvent(
              new CustomEvent('gameVictory', {
                detail: {
                  reason: 'You have won the game!',
                  npc: this.currentNPCName,
                  dialog: this.currentNodeId,
                },
              }),
            );
          } else if (node.isGameOver) {
            window.dispatchEvent(
              new CustomEvent('gameOver', {
                detail: {
                  reason: 'You have failed!!',
                  npc: this.currentNPCName,
                  dialog: this.currentNodeId,
                },
              }),
            );
          }
          this.hide();
        } else if (node.nextDialogId) {
          this.currentNodeId = node.nextDialogId;
          await this.displayCurrentNode();
        } else {
          // Check for victory/game over triggers before closing
          if (node.isVictory) {
            window.dispatchEvent(
              new CustomEvent('gameVictory', {
                detail: {
                  reason: 'You have won the game!',
                  npc: this.currentNPCName,
                  dialog: this.currentNodeId,
                },
              }),
            );
          } else if (node.isGameOver) {
            window.dispatchEvent(
              new CustomEvent('gameOver', {
                detail: {
                  reason: 'You have failed!!',
                  npc: this.currentNPCName,
                  dialog: this.currentNodeId,
                },
              }),
            );
          }
          this.hide();
        }
      };

      // Add click handler to interaction area - this will catch clicks anywhere on the dialog
      this.interactionArea.on('pointerup', continueAction);

      // Add visual feedback when hovering over the entire dialog
      this.interactionArea.on('pointerover', () => {
        this.continueText.tint = dialogConfig.colors.hover;
        if (this.dialogBoxSprite?.visible) {
          this.dialogBoxSprite.alpha = 0.95; // Slightly more transparent on hover
        } else {
          this.background.alpha = 0.98; // Slightly more opaque on hover for graphics fallback
        }
      });

      this.interactionArea.on('pointerout', () => {
        this.continueText.tint = dialogConfig.colors.white;
        if (this.dialogBoxSprite?.visible) {
          this.dialogBoxSprite.alpha = 1.0; // Normal opacity
        } else {
          this.background.alpha = 1.0; // Normal opacity for graphics fallback
        }
      });
    } else {
      // Remove interactivity when there are choices
      this.interactionArea.interactive = false;
      this.interactionArea.cursor = 'default';
      this.interactionArea.alpha = 0; // Hide when not needed
      this.continueText.tint = dialogConfig.colors.white; // Reset color
      if (this.dialogBoxSprite?.visible) {
        this.dialogBoxSprite.alpha = 1.0; // Reset opacity
      } else {
        this.background.alpha = 1.0; // Reset opacity for graphics fallback
      }
    }
  }

  public async handleInput(
    _up: boolean,
    _down: boolean,
    interact: boolean,
    cancel: boolean = false,
  ): Promise<boolean> {
    if (!this.isVisible) return false;

    if(this.isTyping){
      return false;
    }

    // Handle cancel input - close dialog immediately
    if (cancel) {
      this.hide();
      return true;
    }

    // For simple dialogs (no dialog tree), just close on interact
    if (!this.currentDialogTree) {
      if (interact) {
        this.hide();
        return true;
      }
      return false;
    }

    // For backwards compatibility, still handle interact for non-choice dialogs
    if (interact && this.currentDialogTree && this.currentNodeId) {
      const node = this.currentDialogTree.nodes[this.currentNodeId];
      if (node && (!node.choices || node.choices.length === 0)) {
        // Record interaction before any navigation
        this.recordCurrentInteraction();

        if (node.isEnd) {
          // Check for victory/game over triggers before closing
          if (node.isVictory) {
            window.dispatchEvent(
              new CustomEvent('gameVictory', {
                detail: {
                  reason: 'You have won the game!',
                  npc: this.currentNPCName,
                  dialog: this.currentNodeId,
                },
              }),
            );
          } else if (node.isGameOver) {
            window.dispatchEvent(
              new CustomEvent('gameOver', {
                detail: {
                  reason: 'You have failed!!',
                  npc: this.currentNPCName,
                  dialog: this.currentNodeId,
                },
              }),
            );
          }
          this.hide();
        } else if (node.nextDialogId) {
          this.currentNodeId = node.nextDialogId;
          await this.displayCurrentNode();
        } else {
          // Check for victory/game over triggers before closing
          if (node.isVictory) {
            window.dispatchEvent(
              new CustomEvent('gameVictory', {
                detail: {
                  reason: 'You have won the game!',
                  npc: this.currentNPCName,
                  dialog: this.currentNodeId,
                },
              }),
            );
          } else if (node.isGameOver) {
            window.dispatchEvent(
              new CustomEvent('gameOver', {
                detail: {
                  reason: 'You have failed!!',
                  npc: this.currentNPCName,
                  dialog: this.currentNodeId,
                },
              }),
            );
          }
          this.hide();
        }
        return true;
      }


    }



    return false;
  }

  public hide(): void {
    // Record the current interaction first
    this.recordCurrentInteraction();

    // Process all queued actions after dialog interaction is recorded
    this.processActionQueue();

    // Then hide the dialog
    this.hideWithoutRecording();
  }

  private recordCurrentInteraction(): void {
    // Record the interaction with StoryManager
    if (this.currentNPCName && this.currentDialogTree && this.currentNodeId) {
      const node = this.currentDialogTree.nodes[this.currentNodeId];
      if (node) {
        const storyManager = StoryManager.getInstance();

        // Build choices information if available
        let choicesInfo: Array<{ text: string; wasSelected: boolean }> | undefined;
        if (node.choices && node.choices.length > 0) {
          choicesInfo = node.choices.map(choice => ({
            text: choice.text,
            wasSelected: false, // We don't track which choice was selected in this simple implementation
          }));
        }

        storyManager.markNPCInteraction(
          this.currentNPCName,
          this.currentNodeId,
          node.speaker,
          node.text,
          node.characterImage,
          node.backgroundImage,
          node.isEnd,
          choicesInfo,
        );
      }
    }
  }

  private hideWithoutRecording(): void {
    this.isVisible = false;
    this.container.visible = false;
    
    // Show interaction button again when dialog closes
    InputManager.getInstance().setInteractButtonVisible(true);
    InputManager.getInstance().setMobileControlsVisible(true);
    // Clear any active choice timeout
    this.clearChoiceTimeout();

    // Clear any remaining queued actions when dialog is force-closed
    this.clearActionQueue();

    // Clean up all event listeners
    this.container.off('pointerup');
    this.interactionArea.off('pointerup');
    this.interactionArea.off('pointerover');
    this.interactionArea.off('pointerout');
    this.background.off('pointerup');
    this.background.off('pointerover');
    this.background.off('pointerout');
    this.continueText.off('pointerup');
    this.continueText.off('pointerover');
    this.continueText.off('pointerout');

    // Clear backdrop elements
    this.cutsceneContainer.removeChildren();
    this.backgroundSprite = null;
    this.characterSprite = null;

    // Return choice texts to pool and clean up
    this.choiceTexts.forEach(choiceText => {
      this.returnChoiceTextToPool(choiceText);
    });
    this.choiceTexts = [];

    // Return choice buttons to pool and clean up
    this.choiceButtons.forEach(choiceButton => {
      this.returnChoiceButtonToPool(choiceButton);
    });
    this.choiceButtons = [];

    // Return choice backgrounds to pool and clean up
    this.choiceBackgrounds.forEach(choiceBackground => {
      this.returnChoiceBackgroundToPool(choiceBackground);
    });
    this.choiceBackgrounds = [];

    this.choicesContainer.removeChildren();

    // Reset visual states
    this.continueText.tint = dialogConfig.colors.white;
    if (this.dialogBoxSprite) {
      this.dialogBoxSprite.alpha = 1.0;
    }
    this.background.alpha = 1.0;

    // Cancel animation
    if (this.animationId !== null) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }

    // Clear current context
    this.currentNPCName = null;
    this.currentDialogTree = null;
    this.currentNodeId = null;
  }

  public isActive(): boolean {
    return this.isVisible;
  }

  public getContainer(): PIXI.Container {
    return this.container;
  }

  public updateScreenSize(width: number, height: number): void {
    this.screenWidth = width;
    this.screenHeight = height;
    this.updateDialogSize();
  }

  public update(): void {
    if (this.isVisible) {
      // Optimized animation: Use requestAnimationFrame instead of Date.now()
      // for better performance and frame sync
      if (this.animationId === null) {
        const animatePulse = (timestamp: number) => {
          if (!this.isVisible) return;

          const pulse =
            dialogConfig.animation.pulseMin +
            Math.sin(timestamp * dialogConfig.animation.pulseSpeed) *
              dialogConfig.animation.pulseRange;
          this.continueText.alpha = pulse;

          this.animationId = requestAnimationFrame(animatePulse);
        };
        this.animationId = requestAnimationFrame(animatePulse);
      }
    } else if (this.animationId !== null) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
  }

  /**
   * Clean up all resources used by the dialog system
   * Call this when the dialog system is no longer needed
   */
  public destroy(): void {
    // Clear any active choice timeout
    this.clearChoiceTimeout();

    // Cancel any running animations
    if (this.animationId !== null) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }

    // Clear image cache
    this.imageCache.clear();

    // Destroy all pooled choice texts
    this.choiceTextPool.forEach(text => {
      text.destroy();
    });
    this.choiceTextPool = [];

    // Destroy all pooled choice buttons
    this.choiceButtonPool.forEach(button => {
      button.destroy();
    });
    this.choiceButtonPool = [];

    // Destroy all pooled choice backgrounds
    this.choiceBackgroundPool.forEach(background => {
      background.destroy();
    });
    this.choiceBackgroundPool = [];

    // Destroy current choice texts
    this.choiceTexts.forEach(text => {
      text.destroy();
    });
    this.choiceTexts = [];

    // Destroy current choice buttons
    this.choiceButtons.forEach(button => {
      button.destroy();
    });
    this.choiceButtons = [];

    // Destroy current choice backgrounds
    this.choiceBackgrounds.forEach(background => {
      background.destroy();
    });
    this.choiceBackgrounds = [];

    // Remove all event listeners and destroy the container
    this.container.destroy({ children: true, texture: false });
  }
}
